import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { ClickableQrCode } from "@/components/ui/ClickableQrCode";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Lightbox } from "@/pages/material/Lightbox";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { plainTextToMailHtmlFragment } from "@/lib/mailHtml";
import { buildMailtoHref } from "@/lib/orderDraftUtils";
import {
  deleteAllPersonalUploads,
  deletePersonalUpload,
  downloadPersonalUploadsZip,
  formatFileSize,
  getPersonalUploadMobileQr,
  getPersonalUploadUrl,
  isPersonalUploadImage,
  listPersonalUploads,
  sendPersonalUploadMail,
  uploadPersonalFile,
  type PersonalUploadFile,
} from "@/lib/personalUploads";

export interface PersonalUploadDialogProps {
  open: boolean;
  onClose: () => void;
}

const POLL_MS = 3000;

export function PersonalUploadDialog({ open, onClose }: PersonalUploadDialogProps) {
  const [files, setFiles] = useState<PersonalUploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [qrExpiresAt, setQrExpiresAt] = useState<string | null>(null);

  const [lightbox, setLightbox] = useState<{
    url: string;
    filename: string;
    contentType?: string | null;
  } | null>(null);
  const [mailDialogOpen, setMailDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const imageFiles = useMemo(() => files.filter(isPersonalUploadImage), [files]);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const reloadFiles = useCallback(async () => {
    try {
      const data = await listPersonalUploads();
      setFiles(data.files || []);
    } catch {
      /* Polling-Fehler still ignorieren */
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPersonalUploads();
      setFiles(data.files || []);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Dateien konnten nicht geladen werden.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQr = useCallback(async () => {
    setQrLoading(true);
    setQrError(null);
    try {
      const data = await getPersonalUploadMobileQr();
      setQrUri(data.qr_data_uri || null);
      setQrUrl(data.url || null);
      setQrExpiresAt(data.expires_at || null);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "QR-Code konnte nicht erzeugt werden.";
      setQrError(message);
      setQrUri(null);
      setQrUrl(null);
    } finally {
      setQrLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      stopPoll();
      setLightbox(null);
      setMailDialogOpen(false);
      return;
    }
    void loadAll();
    void loadQr();
    return () => {
      stopPoll();
    };
  }, [open, loadAll, loadQr, stopPoll]);

  useEffect(() => {
    if (!open) {
      stopPoll();
      return;
    }
    stopPoll();
    pollRef.current = setInterval(() => {
      void reloadFiles();
    }, POLL_MS);
    return () => {
      stopPoll();
    };
  }, [open, reloadFiles, stopPoll]);

  async function handleDesktopFiles(selected: FileList | null) {
    if (!selected?.length) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(selected)) {
        await uploadPersonalFile(file);
      }
      await reloadFiles();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Upload fehlgeschlagen.";
      setError(message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDelete(fileId: number) {
    try {
      await deletePersonalUpload(fileId);
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Löschen fehlgeschlagen.";
      setError(message);
    }
  }

  async function handleDownloadAllImages() {
    setBulkBusy(true);
    setDownloading(true);
    setError(null);
    try {
      await downloadPersonalUploadsZip(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Download fehlgeschlagen.";
      setError(message);
    } finally {
      setBulkBusy(false);
      setDownloading(false);
    }
  }

  async function handleDeleteAll() {
    if (!files.length) return;
    if (!window.confirm(`Alle ${files.length} Datei(en) wirklich löschen?`)) return;
    setBulkBusy(true);
    setError(null);
    try {
      await deleteAllPersonalUploads();
      setFiles([]);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Löschen fehlgeschlagen.";
      setError(message);
    } finally {
      setBulkBusy(false);
    }
  }

  const qrExpiresLabel = qrExpiresAt
    ? new Date(qrExpiresAt).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="Handy → PC"
        description="Fotos oder Dokumente vom Smartphone hochladen und am PC weiterverwenden."
        size="lg"
        zIndexClass="z-[140]"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Schließen
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          <div
            role="note"
            className="rounded-ios-lg border border-amber-300/80 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
          >
            <p className="font-semibold">Automatische Löschung nach 24 Stunden</p>
            <p className="mt-1 text-[13px] leading-relaxed opacity-90">
              Alle hochgeladenen Dateien werden spätestens 24 Stunden nach dem Upload
              unwiderruflich gelöscht. Bitte rechtzeitig herunterladen, kopieren oder
              per E-Mail versenden.
            </p>
          </div>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-label">Vom Handy hochladen</h3>
            {qrLoading ? (
              <div className="flex items-center justify-center gap-2 py-6">
                <Spinner />
                <span className="text-sm text-label-secondary">QR-Code wird erzeugt …</span>
              </div>
            ) : null}
            {qrError ? <p className="text-sm text-status-red">{qrError}</p> : null}
            {qrUri ? (
              <div className="flex flex-col items-center gap-3 rounded-ios-lg border border-separator bg-fill-tertiary/60 px-4 py-5">
                <ClickableQrCode
                  src={qrUri}
                  href={qrUrl}
                  alt="QR-Code für Handy-Upload"
                  className="h-40 w-40 rounded-ios border border-separator bg-white p-2 shadow-sm"
                />
                <p className="text-center text-xs text-label-secondary">
                  QR-Code mit dem Smartphone scannen
                </p>
                {qrExpiresLabel ? (
                  <p className="text-center text-xs text-label-tertiary">
                    QR-Link gültig bis {qrExpiresLabel} Uhr
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-label">Am PC</h3>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  className="hidden"
                  onChange={(e) => void handleDesktopFiles(e.target.files)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? "Wird hochgeladen …" : "Dateien hochladen"}
                </Button>
              </div>
            </div>
            <p className="text-xs text-label-tertiary">
              Unterstützt: Fotos (PNG, JPEG, WebP, HEIC) und PDF bis 20 MB. Nur Sie sehen
              Ihre Dateien.
            </p>
          </section>

          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-label">
                Ihre Dateien ({files.length})
              </h3>
              {files.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={bulkBusy || imageFiles.length === 0}
                    onClick={() => void handleDownloadAllImages()}
                  >
                    {downloading ? "Wird heruntergeladen …" : "Alle Bilder herunterladen"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={bulkBusy || files.length === 0}
                    onClick={() => setMailDialogOpen(true)}
                  >
                    Per Mail versenden
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={bulkBusy || files.length === 0}
                    onClick={() => void handleDeleteAll()}
                  >
                    Alle löschen
                  </Button>
                </div>
              ) : null}
            </div>
            {loading ? (
              <div className="flex items-center gap-2 py-4">
                <Spinner />
                <span className="text-sm text-label-secondary">Lade …</span>
              </div>
            ) : null}
            {error ? <p className="text-sm text-status-red">{error}</p> : null}
          {successMessage ? (
            <p className="text-sm text-status-green">{successMessage}</p>
          ) : null}
            {!loading && files.length === 0 ? (
              <p className="rounded-ios-lg border border-dashed border-separator px-4 py-6 text-center text-sm text-label-tertiary">
                Noch keine Dateien. Scannen Sie den QR-Code oder laden Sie Dateien am PC hoch.
              </p>
            ) : null}
            {files.length > 0 ? (
              <ul className="grid gap-3 sm:grid-cols-2">
                {files.map((file) => (
                  <PersonalUploadFileCard
                    key={file.id}
                    file={file}
                    onPreview={() =>
                      setLightbox({
                        url: getPersonalUploadUrl(file.id),
                        filename: file.filename || "bild.jpg",
                        contentType: file.content_type,
                      })
                    }
                    onDelete={() => void handleDelete(file.id)}
                  />
                ))}
              </ul>
            ) : null}
          </section>
        </div>
      </Dialog>

      <PersonalUploadMailDialog
        open={mailDialogOpen}
        files={files}
        onClose={() => setMailDialogOpen(false)}
        onSent={(message) => {
          setSuccessMessage(message);
          setError(null);
        }}
      />

      <Lightbox
        url={lightbox?.url ?? null}
        copySource={
          lightbox
            ? {
                url: lightbox.url,
                filename: lightbox.filename,
                contentType: lightbox.contentType || "image/jpeg",
              }
            : null
        }
        onClose={() => setLightbox(null)}
        zIndexClass="z-[230]"
      />
    </>
  );
}

function PersonalUploadFileCard({
  file,
  onPreview,
  onDelete,
}: {
  file: PersonalUploadFile;
  onPreview: () => void;
  onDelete: () => void;
}) {
  const isImage = isPersonalUploadImage(file);
  const previewUrl = getPersonalUploadUrl(file.id);
  const downloadUrl = getPersonalUploadUrl(file.id, true);
  const expiresLabel = file.expires_at
    ? new Date(file.expires_at).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <li className="flex flex-col overflow-hidden rounded-ios-lg border border-separator bg-fill-tertiary/40">
      {isImage ? (
        <button
          type="button"
          onClick={onPreview}
          className="block aspect-[4/3] w-full overflow-hidden bg-fill-tertiary"
        >
          <img
            src={previewUrl}
            alt={file.filename}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </button>
      ) : (
        <div className="flex aspect-[4/3] items-center justify-center bg-fill-tertiary text-4xl">
          📄
        </div>
      )}
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-label" title={file.filename}>
            {file.filename}
          </p>
          <p className="text-xs text-label-tertiary">
            {formatFileSize(file.size_bytes)}
            {expiresLabel ? ` · löscht um ${expiresLabel} Uhr` : null}
          </p>
        </div>
        <div className="mt-auto flex flex-wrap gap-2">
          <a
            href={downloadUrl}
            download={file.filename}
            className="inline-flex items-center rounded-ios border border-separator bg-fill-primary px-2.5 py-1 text-xs font-medium text-label hover:bg-fill-secondary"
          >
            Herunterladen
          </a>
          {isImage ? (
            <button
              type="button"
              onClick={onPreview}
              className="inline-flex items-center rounded-ios border border-separator bg-fill-primary px-2.5 py-1 text-xs font-medium text-label hover:bg-fill-secondary"
            >
              Öffnen
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center rounded-ios border border-separator px-2.5 py-1 text-xs font-medium text-status-red hover:bg-fill-secondary"
          >
            Löschen
          </button>
        </div>
      </div>
    </li>
  );
}

function PersonalUploadMailDialog({
  open,
  files,
  onClose,
  onSent,
}: {
  open: boolean;
  files: PersonalUploadFile[];
  onClose: () => void;
  onSent?: (message: string) => void;
}) {
  const { user } = useCurrentUser();
  const isEntra = String(user?.auth_provider || "").toLowerCase() === "entra";

  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("Fotos vom Handy");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(files.map((f) => f.id)));
    setError(null);
    setSending(false);
  }, [open, files]);

  const selectedFiles = useMemo(
    () => files.filter((f) => selected.has(f.id)),
    [files, selected],
  );

  const graphRecipientReady = to.trim().length > 0;
  const mailReady = subject.trim().length > 0;

  function toggleFile(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleDirectSend() {
    if (!selectedFiles.length) {
      setError("Bitte mindestens eine Datei auswählen.");
      return;
    }
    if (!graphRecipientReady) {
      setError("Empfänger-E-Mail-Adresse erforderlich.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const htmlBody = plainTextToMailHtmlFragment(body);
      await sendPersonalUploadMail({
        file_ids: selectedFiles.map((f) => f.id),
        to: to.trim(),
        subject: subject.trim(),
        body,
        html_body: htmlBody,
      });
      onSent?.("Mail gesendet.");
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 412) {
        setError("Microsoft-Anmeldung muss erneuert werden. Bitte neu anmelden.");
      } else {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Versand fehlgeschlagen.";
        setError(message);
      }
    } finally {
      setSending(false);
    }
  }

  function handleMailtoOpen() {
    if (!mailReady) {
      setError("Betreff ist erforderlich.");
      return;
    }
    const href = buildMailtoHref(to.trim(), subject.trim(), body);
    const a = document.createElement("a");
    a.href = href;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    if (selectedFiles.length) {
      setError(
        "Hinweis: Anhänge können im Mailprogramm nicht automatisch angefügt werden. Bitte Dateien manuell hinzufügen oder Direktversand (Microsoft) nutzen.",
      );
    } else {
      onClose();
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Per Mail versenden"
      description="Ausgewählte Dateien direkt über Ihr Microsoft-Postfach versenden."
      size="md"
      zIndexClass="z-[200]"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!mailReady || sending}
            onClick={handleMailtoOpen}
          >
            Im Mail-Programm öffnen
          </Button>
          <Button
            type="button"
            disabled={!mailReady || !graphRecipientReady || sending || selectedFiles.length === 0}
            loading={sending}
            title={!graphRecipientReady ? "Empfänger-E-Mail-Adresse erforderlich" : undefined}
            onClick={() => void handleDirectSend()}
          >
            Direkt senden (Microsoft)
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {isEntra ? (
          <p className="text-xs text-label-secondary">
            Mit Microsoft angemeldet: Direktversand erfolgt über Ihr eigenes Postfach als HTML-Mail
            mit den ausgewählten Anhängen.
          </p>
        ) : (
          <p className="text-xs text-label-secondary">
            Für den Direktversand mit Anhängen ist eine Anmeldung mit Microsoft erforderlich.
          </p>
        )}

        <TextInput
          label="An"
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          hint={!graphRecipientReady ? "Für Direktversand ist eine Empfänger-Adresse erforderlich." : undefined}
        />
        <TextInput
          label="Betreff"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Nachricht (optional)</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            className="w-full rounded-ios border border-separator bg-fill-primary px-3 py-2 text-sm text-label"
          />
        </label>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-label">Anhänge</p>
            <button
              type="button"
              className="text-xs font-medium text-accent hover:underline"
              onClick={() =>
                setSelected(
                  selected.size === files.length
                    ? new Set()
                    : new Set(files.map((f) => f.id)),
                )
              }
            >
              {selected.size === files.length ? "Keine auswählen" : "Alle auswählen"}
            </button>
          </div>
          <ul className="max-h-48 space-y-1 overflow-y-auto rounded-ios border border-separator p-2">
            {files.map((file) => (
              <li key={file.id}>
                <label className="flex cursor-pointer items-center gap-2 rounded-ios px-2 py-1.5 hover:bg-fill-tertiary">
                  <input
                    type="checkbox"
                    checked={selected.has(file.id)}
                    onChange={() => toggleFile(file.id)}
                    className="rounded border-separator"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-label">
                    {file.filename}
                  </span>
                  <span className="shrink-0 text-xs text-label-tertiary">
                    {formatFileSize(file.size_bytes)}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </div>
    </Dialog>
  );
}
